import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Button, Panel, StateSurface } from '../design-system/index.js';
import { WorkspaceProvider, type SessionResource, type WorkspaceResource } from './runtime.js';

function BoundaryFrame({ children }: { children: ReactNode }) {
  return <main className="grid min-h-svh place-items-center bg-bg p-5 text-ink">{children}</main>;
}

function LoadingBoundary({ label }: { label: string }) {
  return (
    <BoundaryFrame>
      <StateSurface kind="loading" title={label} className="w-full max-w-lg" />
    </BoundaryFrame>
  );
}

function FailureBoundary({
  title,
  copy,
  retry,
  retrying = false,
}: {
  title: string;
  copy: string;
  retry: (() => void) | undefined;
  retrying?: boolean;
}) {
  return (
    <BoundaryFrame>
      <Panel
        className="w-full max-w-lg rounded-xl border border-border bg-surface p-6"
        aria-labelledby="boundary-error-title"
      >
        <AlertTriangle className="mb-4 size-6 text-err" aria-hidden="true" />
        <h1 id="boundary-error-title" className="text-xl font-semibold">
          {title}
        </h1>
        <p className="mt-2 text-sm leading-6 text-ink-2">{copy}</p>
        {retry ? (
          <Button variant="secondary" onClick={retry} className="mt-5" disabled={retrying}>
            <RefreshCw className="size-4" aria-hidden="true" />
            {retrying ? 'Trying again…' : 'Try again'}
          </Button>
        ) : null}
      </Panel>
    </BoundaryFrame>
  );
}

export function SessionLoadingBoundary() {
  return <LoadingBoundary label="Opening Song Composer…" />;
}

export function SessionUnavailableBoundary({
  resource,
}: {
  resource: Extract<SessionResource, { status: 'unavailable' }>;
}) {
  return (
    <FailureBoundary
      title="Song Composer is temporarily unavailable."
      copy="The session service could not be reached. Check your connection and try again."
      retry={resource.retry}
      retrying={resource.retrying}
    />
  );
}

export function SessionBoundary({ resource }: { resource: SessionResource }) {
  const location = useLocation();

  if (resource.status === 'loading') return <SessionLoadingBoundary />;
  if (resource.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (resource.status === 'unavailable') return <SessionUnavailableBoundary resource={resource} />;

  return <Outlet context={{ session: resource.session }} />;
}

export function WorkspaceBoundary({ resource }: { resource: WorkspaceResource }) {
  if (resource.status === 'loading') return <LoadingBoundary label="Resolving workspace…" />;
  if (resource.status === 'empty') {
    return (
      <FailureBoundary
        title="Your workspace isn’t ready."
        copy="Finish setup before opening Song Composer."
        retry={undefined}
      />
    );
  }
  if (resource.status === 'error') {
    return (
      <FailureBoundary
        title="We couldn’t load your workspace."
        copy="Check your connection and try again."
        retry={resource.retry}
      />
    );
  }

  return (
    <WorkspaceProvider workspace={resource.workspace}>
      <Outlet />
    </WorkspaceProvider>
  );
}
